import {
  CrewMerchantApi,
  CrewMerchantUi,
  isNullish,
  stringToJSON,
} from 'corso-types';
import { type SortMenuOption } from 'corso-admin-ui';
import { computeDateFilterValue } from 'corso-web-lib';
import isEqual from 'lodash.isequal';
import {
  createContext,
  Dispatch,
  PropsWithChildren,
  useContext,
  useEffect,
  useReducer,
} from 'react';
import { match } from 'ts-pattern';
import { useLocalStorage } from 'usehooks-ts';
import { z } from 'zod';
import { useStoreId } from '~/providers/StoreUserProvider';
import {
  Filter,
  FilterControlType,
  FilterDefinition,
  useFilterDefinition,
} from './useFilterDefinition';
import { useSearchViews } from './useSearchViewApi';

// todo find a home for this
const useSortOptions = () => [{ value: 'createdOn', label: 'Date' }];

const sortValueSchema = z.object({
  column: z.string(),
  direction: z.enum(['asc', 'desc']),
});
type SortValue = z.infer<typeof sortValueSchema>;

export type State = {
  kind: CrewMerchantUi.SearchView['kind'];
  sortOptions: SortMenuOption[];
  orderBy: SortValue;
  showFilters: boolean;
  filterDefinition: FilterDefinition;
  viewModified: boolean;
  views: CrewMerchantUi.SearchView[];
  view: CrewMerchantUi.SearchView;
  cursor: CrewMerchantApi.RequestBody<'/:storeId/search', 'post'>['cursor'];
};

export type SearchControlAction =
  | {
      type: 'toggleFilters';
      show?: boolean;
    }
  | {
      type: 'filterChange';
      filter: Filter;
    }
  | {
      type: 'removeFilters';
      ids?: Filter['id'][];
    }
  | {
      type: 'sortChange';
      orderBy: SortValue;
    }
  | {
      type: 'viewChange';
      view: State['view'] | null;
    }
  | {
      type: 'setCursor';
      cursor: CrewMerchantApi.RequestBody<'/:storeId/search', 'post'>['cursor'];
    }
  | {
      type: 'setFilterDefinition';
      filterDefinition: FilterDefinition;
    }
  | {
      type: 'setViews';
      views: State['views'];
    };

export const LOCAL_VIEW_ID = Number.MIN_SAFE_INTEGER;
const createLocalView = <Entity extends CrewMerchantUi.SearchView['kind']>(
  entity: Entity,
) =>
  ({
    // maybe better named as All view?
    id: LOCAL_VIEW_ID,
    name: 'Local View',
    kind: entity,
    filters: {},
  }) satisfies CrewMerchantUi.SearchView;

const isViewModified = (
  view: CrewMerchantUi.SearchView,
  views: CrewMerchantUi.SearchView[],
) => {
  if (view.id === LOCAL_VIEW_ID) {
    return Object.values(view.filters).some((value) => !!value);
  }

  const originalView = views.find((v) => v.id === view.id);
  if (!originalView) {
    return true;
  }

  return !isEqual(originalView.filters, view.filters);
};

const resetView = (view: State['view'], views: State['views']) => {
  if (view.id === LOCAL_VIEW_ID) {
    return createLocalView(view.kind);
  }

  const originalView = views.find((v) => v.id === view.id);

  return originalView ?? createLocalView(view.kind);
};

const searchReducer = (state: State, action: SearchControlAction): State =>
  match<SearchControlAction, State>(action)
    .with({ type: 'setCursor' }, ({ cursor }) => ({
      ...state,
      cursor,
    }))
    .with({ type: 'toggleFilters' }, ({ show }) => {
      const showFilters = show ?? !state.showFilters;
      const view = showFilters
        ? state.view
        : resetView(state.view, state.views);

      return {
        ...state,
        view,
        viewModified: false,
        showFilters,
      };
    })
    .with({ type: 'filterChange' }, ({ filter: { id, value } }) => {
      const view = {
        ...state.view,
        filters: {
          ...state.view.filters,
          [id]: value,
        },
      } satisfies CrewMerchantUi.SearchView;

      return {
        ...state,
        view,
        viewModified: isViewModified(view, state.views),
        cursor: null, // reset cursor when filters change
      };
    })
    .with({ type: 'removeFilters' }, ({ ids }) => {
      const updatedFilters: CrewMerchantUi.SearchView['filters'] =
        // no ids provided, clear all filters
        ids
          ? Object.fromEntries(
              Object.entries(state.view.filters).filter(
                ([id]) => !ids.includes(id as keyof typeof state.view.filters),
              ),
            )
          : {};

      const view = {
        ...state.view,
        filters: updatedFilters,
      } satisfies CrewMerchantUi.SearchView;

      return {
        ...state,
        view,
        viewModified: isViewModified(view, state.views),
        cursor: null, // reset cursor when filters change
      };
    })
    .with({ type: 'sortChange' }, ({ orderBy }) => ({
      ...state,
      orderBy,
    }))
    .with({ type: 'viewChange' }, ({ view }) => ({
      ...state,
      orderBy: {
        column: state.sortOptions[0]?.value ?? '',
        direction: state.orderBy.direction,
      },
      view: view ?? createLocalView(state.kind),
      viewModified: false,
      showFilters: false,
      cursor: null, // reset cursor when view changes
    }))
    .with({ type: 'setFilterDefinition' }, ({ filterDefinition }) => ({
      ...state,
      filterDefinition,
    }))
    .with({ type: 'setViews' }, ({ views }) => {
      const view = resetView(state.view, views);
      return {
        ...state,
        views,
        view,
        viewModified: isViewModified(view, views),
      };
    })
    .exhaustive();

const SearchStateContext = createContext<State | null>(null);
const SearchDispatchContext =
  createContext<Dispatch<SearchControlAction> | null>(null);

export const useSearchState = () => {
  const state = useContext(SearchStateContext);
  const dispatch = useContext(SearchDispatchContext);

  if (state === null || dispatch === null) {
    throw new Error('useSearchContext must be used within a SearchProvider');
  }
  return [state, dispatch] as const;
};

export function SearchProvider({
  entity,
  children,
}: PropsWithChildren<{ entity: CrewMerchantUi.SearchView['kind'] }>) {
  const storeId = useStoreId();
  const filterDefinition = useFilterDefinition(entity);
  const { data: views } = useSearchViews(entity);
  const sortOptions = useSortOptions();
  const initialState: State = {
    kind: entity,
    sortOptions,
    orderBy: { column: sortOptions[0]?.value ?? '', direction: 'desc' },
    showFilters: false,
    filterDefinition,
    viewModified: false,
    views: views ?? [],
    view: createLocalView(entity),
    cursor: null,
  };
  // ? do we really need to persist this, useful for preserving current view/filters when switching between entities
  const [persistedState, saveState] = useLocalStorage<State>(
    `${storeId}-${entity}-search`,
    initialState,
    {
      serializer: ({ view, orderBy, showFilters, viewModified }) =>
        JSON.stringify({ view, orderBy, showFilters, viewModified }),
      deserializer: (serialized) => {
        const parsed = stringToJSON()
          .pipe(
            z.object({
              view: CrewMerchantUi.searchViewSchema,
              orderBy: sortValueSchema,
              showFilters: z.boolean(),
              viewModified: z.boolean(),
            }),
          )
          .safeParse(serialized);

        const notValid =
          !parsed.success ||
          parsed.data.view.kind !== entity ||
          (parsed.data.view.id !== LOCAL_VIEW_ID &&
            !views?.find((v) => v.id === parsed.data.view.id));

        if (notValid) {
          console.warn(`Invalid ${entity} SearchControlBar State`, {
            parsed,
            error: parsed.error,
          });

          return initialState;
        }

        return {
          ...initialState,
          ...parsed.data,
          filterDefinition,
        };
      },
    },
  );

  const [state, dispatch] = useReducer(searchReducer, persistedState);

  // update state with the latest filter definition for async filters options
  useEffect(() => {
    if (!isEqual(state.filterDefinition, filterDefinition)) {
      dispatch({
        type: 'setFilterDefinition',
        filterDefinition,
      });
    }
  }, [filterDefinition, state.filterDefinition]);

  // update state with the resolved views from the API
  useEffect(() => {
    if (views && !isEqual(state.views, views)) {
      dispatch({
        type: 'setViews',
        views,
      });
    }
  }, [views, state.views]);

  // persist state changes to local storage
  useEffect(() => {
    saveState(state);
  }, [state, saveState]);

  return (
    <SearchStateContext.Provider value={state}>
      <SearchDispatchContext.Provider value={dispatch}>
        {children}
      </SearchDispatchContext.Provider>
    </SearchStateContext.Provider>
  );
}

// TODO automation-like type safety for filters
// ! this allows us to lie in the types, because there's no type-enforced relationship between the filter and it's resulting type, anything additional properties can really be almost anything; i.e. any arbitrary key can be added with any value, and duck typing says it's fine
export const convertStateToSearch = (
  state: State,
): Omit<Extract<CrewMerchantUi.SearchReq, { kind: State['kind'] }>, 'take'> => {
  const {
    view,
    filterDefinition: { filters },
  } = state;

  const filterIds = Object.keys(
    view.filters,
  ) as unknown as (keyof typeof view.filters)[];

  const searchFilters = filterIds.reduce((search, id) => {
    if (isNullish(view.filters[id])) return search;

    // oxlint-disable-next-line typescript/no-unnecessary-condition -- ignoring for a pessimistic check, although this should be fine, but we would want to verify all of the type assertions
    if (id !== 'searchTerm' && filters[id]?.type === FilterControlType.date) {
      return {
        ...search,
        //! TODO: gotta figure out to the view values related to the filter definition
        [id]: computeDateFilterValue(view.filters[id]),
      };
    }

    return { ...search, [id]: view.filters[id] };
  }, {});

  return {
    orderBy: state.orderBy.direction,
    filters: searchFilters,
    kind: state.kind,
    cursor: state.cursor,
    shouldUseOpenSearch: false,
  };
};
